import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Paintbrush,
  Palette,
  LayoutTemplate,
  Plug,
  CreditCard,
  ShieldCheck,
  Receipt,
  Bell,
  ChevronDown,
  LogOut,
  User,
  X,
  Menu,
  Users,
  Globe,
  Layers,
  DollarSign,
  Wallet,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';
import { useRole, useIsSuperAdmin } from '@/hooks/useRole';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/studio', label: 'Site Builder', icon: Paintbrush },
  { to: '/brands', label: 'Brand Manager', icon: Palette },
  { to: '/templates', label: 'Templates', icon: LayoutTemplate },
  { to: '/integrations', label: 'Integrations', icon: Plug },
  { to: '/billing', label: 'Billing', icon: CreditCard },
  { to: '/admin/sites', label: 'Admin Settings', icon: ShieldCheck },
  { to: '/admin/ai-spend', label: 'AI Spend', icon: Receipt },
  { to: '/team', label: 'Team Members', icon: Users },
];

const rootNavItems = [
  { to: '/root/users',     label: 'Accounts',  icon: Users },
  { to: '/root/websites',  label: 'Websites',  icon: Globe },
  { to: '/admin/ai-spend', label: 'AI Spend',  icon: Receipt },
  { to: '/root/templates', label: 'Templates', icon: Layers },
  { to: '/root/payments',  label: 'Payments',  icon: DollarSign },
  { to: '/root/wallets',   label: 'Wallets',   icon: Wallet },
];

function StaffSidebar({ onClose }: { onClose?: () => void }) {
  const isSuperAdmin = useIsSuperAdmin();

  return (
    <div className="flex h-full flex-col" style={{ background: '#10032A' }}>
      {/* Header */}
      <div
        className="flex h-14 items-center justify-between px-4 border-b"
        style={{ borderColor: '#281C3F' }}
      >
        <div className="flex items-center gap-2">
          <div
            className="flex h-7 w-7 items-center justify-center rounded text-sm font-bold"
            style={{ background: '#C9B8F4', color: '#10032A' }}
          >
            BL
          </div>
          <span className="font-semibold text-white">Bread List Sites</span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="rounded-md p-1 lg:hidden text-brand-lavender/60 hover:text-brand-lavender"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4">
        <div className="px-3 space-y-1">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-brand-plum text-brand-lavender'
                    : 'text-brand-lavender/60 hover:bg-brand-plum hover:text-brand-lavender'
                )
              }
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Root section — superadmin only */}
      {isSuperAdmin && (
        <div style={{ background: '#1a0a35' }}>
          {/* Separator + label */}
          <div className="flex items-center gap-2 px-4 pt-3 pb-1">
            <div className="flex-1 border-t border-brand-plum-deep" />
            <span
              className="text-[10px] font-semibold tracking-widest text-brand-lavender/60"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              ROOT
            </span>
            <div className="flex-1 border-t border-brand-plum-deep" />
          </div>

          <div className="px-3 pb-3 space-y-1">
            {rootNavItems.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-brand-plum text-brand-lavender'
                      : 'text-brand-lavender/60 hover:bg-brand-plum hover:text-brand-lavender'
                  )
                }
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                {label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StaffTopBar({ onMenuClick }: { onMenuClick: () => void }) {
  const { email } = useAuthStore();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const role = useRole();

  const roleBadgeLabel = role === 'admin' ? 'Admin' : 'Staff';

  return (
    <header className="sticky top-0 z-10 flex h-14 items-center border-b bg-white px-4 shadow-sm">
      <button
        onClick={onMenuClick}
        className="mr-3 rounded-md p-1.5 text-gray-500 hover:bg-gray-100 lg:hidden"
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Wordmark */}
      <div className="flex items-center gap-2">
        <span className="hidden font-semibold text-gray-900 sm:block">Bread List Sites</span>
      </div>

      <div className="flex-1" />

      <div className="flex items-center gap-3">
        {/* Role badge */}
        <span className="hidden rounded-full bg-brand-sand/50 px-2.5 py-0.5 text-xs font-medium text-brand-sand-deep sm:inline-flex">
          {roleBadgeLabel}
        </span>

        {/* Notifications */}
        <button className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100" aria-label="Notifications">
          <Bell className="h-5 w-5" />
        </button>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-medium">
                {email ? email[0].toUpperCase() : 'U'}
              </div>
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => navigate('/profile')}>
              <User className="mr-2 h-4 w-4" />
              My Profile
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-brand-error">
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export function StaffLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen bg-background" data-shell="staff">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 w-64 transform transition-transform duration-200 ease-in-out',
          'lg:static lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <StaffSidebar onClose={() => setSidebarOpen(false)} />
      </aside>

      <main className="flex-1 overflow-auto">
        <StaffTopBar onMenuClick={() => setSidebarOpen(true)} />
        <Outlet />
      </main>
    </div>
  );
}
